import { useCallback, useState } from 'react';
import { Job } from 'api/domain/entities/Job';
import { useNavigate } from 'react-router-dom';
import { ExcludedServiceCodesFromPostWorkQuestionnaire } from 'common/data/service';

import { Service } from 'domain/entities/Service';
import { isEntityLocked } from 'domain/entities/BillingStatus';
import { ServiceStatus } from 'domain/entities/ServiceStatus';
import { ID } from 'domain/types/ID';
import { useCaseUpdateService } from 'application/services/useCases/useCaseUpdateService';
import { useCaseCancelService } from 'application/services/useCases/useCaseCancelService';
import usePermission from 'application/auth/hooks/usePermission';
import { useCaseCompleteService } from 'application/services/useCases/useCaseCompleteService';
import { JobResourceType } from 'application/auth/utils/resources';
import { useTranslationPrefix } from 'infrastructure/translations/i18n';
import useSnackbar from 'targets/web/modules/dashboard/hooks/useSnackbar';
import { CancelJobModal } from 'targets/web/modules/jobs/components/CancelJobModal';

import { StatusChip } from './StatusChip';

type ServiceStatusChipProps = Pick<
  Service,
  'id' | 'status' | 'scheduledDue' | 'billingStatus' | 'jobId' | 'code'
> & {
  jobBillingStatus: Job['billingStatus'];
  availableStatusChanges: ServiceStatus[];
  onChange?: (status: 'submitted' | 'in_progress' | 'completed') => void;
  optimisticPermissions?: boolean;
};

export const ServiceStatusChip = ({
  id,
  jobId,
  status,
  scheduledDue,
  billingStatus,
  jobBillingStatus,
  onChange,
  availableStatusChanges,
  code,
  optimisticPermissions,
}: ServiceStatusChipProps) => {
  const notify = useSnackbar();
  const navigate = useNavigate();
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  const { updateService } = useCaseUpdateService();
  const { completeService, isPending } = useCaseCompleteService();
  const { cancelService } = useCaseCancelService();
  const t = useTranslationPrefix('components.cards');
  const tCancel = useTranslationPrefix('jobs.service_details.cancel_service_modal.snackbar');
  const editable =
    !isEntityLocked({ billingStatus, status }) &&
    !isEntityLocked({ billingStatus: jobBillingStatus, status });

  const { granted: editGranted } = usePermission(
    editable
      ? {
          resource: `${JobResourceType}:${jobId}`,
          scope: 'update-statuses',
        }
      : undefined,
    optimisticPermissions,
  );

  const cancelAvailable = editable && status !== 'completed' && status !== 'canceled';
  const { granted: cancelGranted } = usePermission(
    cancelAvailable
      ? {
          resource: `${JobResourceType}:${jobId}`,
          scope: 'cancel',
        }
      : undefined,
    optimisticPermissions,
  );

  const handleStatusChipChange = useCallback(
    async (status: 'submitted' | 'in_progress' | 'completed') => {
      if (status === 'completed') {
        if (ExcludedServiceCodesFromPostWorkQuestionnaire.includes(code)) {
          completeService({
            serviceId: ID(id),
          }).catch((error) => {
            notify(
              t('serviceCard.complete_error', {
                error: error?.body?.message || error?.message || 'unknown',
              }),
              { variant: 'error' },
            );
          });
        } else {
          navigate(`/jobs/${jobId}/services/${id}/post-work-questionnaire`);
        }
      } else {
        await updateService(
          {
            serviceId: id,
            data: {
              status: status === 'submitted' ? 'pending' : status,
            },
          },
          {
            onError: () => {
              notify("Error occurred while updating the Service's status", {
                variant: 'error',
              });
            },
          },
        );
      }

      onChange?.(status);
    },
    [onChange, t, id, notify, updateService, jobId, navigate, code, completeService],
  );

  const handleCancelClick = useCallback(() => {
    setIsCancelModalOpen(true);
  }, []);

  return (
    <>
      <StatusChip
        status={status}
        dueDate={scheduledDue}
        editable={editable && editGranted && !isPending}
        availableStatusChanges={[
          ...availableStatusChanges,
          ...(cancelAvailable && cancelGranted ? (['canceled'] as const) : []),
        ]}
        onSelect={handleStatusChipChange}
        onCancel={cancelAvailable && cancelGranted ? handleCancelClick : undefined}
      />
      <CancelJobModal
        type="service"
        open={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        onSubmit={(values) => {
          return cancelService({
            serviceId: ID(id),
            cancel: {
              reason: values.reason,
              notes: values.reason === 'other' ? values.notes : '',
            },
          }).then(
            () => {
              notify(tCancel('cancel_success'), { variant: 'secondary' });
              onChange?.('submitted'); // trigger refetch
            },
            () => {
              notify(tCancel('cancel_error'), { variant: 'error' });
            },
          );
        }}
      />
    </>
  );
};
